import { Component } from 'react';
import GameData from "../mobx/GameData";
import { observe } from "mobx";
import { Button, Card, Form } from "react-bootstrap";
import ApplicationState from "../mobx/ApplicationState";
import { Container, Row, Col, ListGroup, Modal } from 'react-bootstrap';
import { coerceUnknownFields } from '../data/BaseGame';
import StaticScreenPositions, { getScreenPositionName, getScreenShowAtDetail } from '../data/StaticScreenPositions';
import { NamEditor } from '../components/nes/NamEditor';

import './StaticScreenPage.scss';
import { v4 } from 'uuid';
import { LinkContainer } from 'react-router-bootstrap';

export default class StaticScreenPage extends Component {

    gameDataCleanup = null;
    confirmData = {
        resolve: null,
        reject: null,
        promise: null
    };


    constructor(props) {
        super(props);

        const gameData = coerceUnknownFields(GameData.currentGame);

        this.state = {
            selectedScreen: null,
            currentScreenInfo: {},
            staticScreens: this.sortScreens(gameData.staticScreens),
            forceUpdateVersionId: 0,

            awaitingConfirm: false,
            awaitingConfirmDelete: false,
            awaitingConfirmAction: 'Do it'
        }
    }

    componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
    }

    sortScreens(list) {
        let newList = list.filter(a =>  a.showAt !== StaticScreenPositions.AfterScreen),
            afterScreens = list.filter(a => a.showAt === StaticScreenPositions.AfterScreen);
        
        newList = newList.sort((a, b) => this.getScreenValue(a) - this.getScreenValue(b));

        for (let i = 0; i < afterScreens.length; i++) {
            let index = newList.findIndex(a => a.id === afterScreens[i].showAtDetail);
            if (index === -1) {
                console.info('Failed positioning a screen, sorting may be wrong.', i, newList, afterScreens[i].showAtDetail);
                index = afterScreens.length;
            }
            newList.splice(index+1, 0, afterScreens[i]);
        }
        return newList;
    }

    getScreenValue(screen) {
        switch (screen.showAt) {
            case StaticScreenPositions.AfterPuzzleSplash: return 0;
            case StaticScreenPositions.AfterTitle: return 100;
            case StaticScreenPositions.AfterEndScreen: return 50000;
            case StaticScreenPositions.AfterCreditsScreen: return 51000;
            case StaticScreenPositions.Unused: return 999999;
            default: 
                if (screen.showAt < 64) {
                    return 200 + (screen.showAtDetail * 10);
                } else {
                    return null;
                }
        }
    }

    showConfirm(action) {
        this.confirmData.promise = new Promise((resolve, reject) => {
            this.confirmData.resolve = resolve;
            this.confirmData.reject = reject;
        });
        this.setState({
            awaitingConfirm: true,
            awaitingConfirmAction: action
        });

        return this.confirmData.promise;
    }

    showConfirmDelete() {
        this.confirmData.promise = new Promise((resolve, reject) => {
            this.confirmData.resolve = resolve;
            this.confirmData.reject = reject;
        });
        this.setState({
            awaitingConfirmDelete: true
        });

        return this.confirmData.promise;
    }

    cancelConfirm() {
        this.confirmData.reject();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false, awaitingConfirmDelete: false})
    }

    continueConfirm() {
        this.confirmData.resolve();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false, awaitingConfirmDelete: false})
    }


    fieldChanged(name) {
        let showAt = this.state.currentScreenInfo.showAt;
        let showAtDetail = this.state.currentScreenInfo.showAtDetail;
        let title = this.state.currentScreenInfo.title;
        let value = document.getElementById(name).value;
        
        if (name === 'showAtDetail') {
            if (this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterLevel) {
                showAtDetail = parseInt(value, 10);
            } else if (this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterScreen) {
                showAtDetail = value;
            }
        } else if (name === 'showAt') {
            value = parseInt(value, 10);
            showAt = value;

            if (value === StaticScreenPositions.AfterLevel) {
                showAtDetail = 0;
            } else if (value === StaticScreenPositions.AfterScreen) {
                showAtDetail = this.state.staticScreens[0].id;
            }
        } else if (name === 'title') {
            title = value;
        } else {
            console.info('Unknown field changed', name);
        } 

        this.setState({currentScreenInfo: {
            ...this.state.currentScreenInfo,
            showAt,
            showAtDetail,
            title
        }});
        this.forceUpdate();
        ApplicationState.setCurrentPageUnsaved();
    }

    async selectScreen(id) {
        if (ApplicationState.currentPageHasUnsavedChanges) {
            try {
                await this.showConfirm('Continue');
            } catch (e) { /* user cancelled, ignore and bail. */ return; }
        }
        ApplicationState.unsetCurrentPageUnsaved();

        this.setState({
            selectedScreen: id,
            currentScreenInfo: JSON.parse(JSON.stringify(this.state.staticScreens.find(a => a.id === id)))
        }, () => this.forceUpdate());
    }

    async deleteCurrentScreen() {
        try {
            await this.showConfirmDelete()
        } catch (e) { /* User aborted, so imma head out */ return; }

        const newScreens = JSON.parse(JSON.stringify(this.state.staticScreens));
        const currentScreenIndex = newScreens.findIndex(a => a.id === this.state.currentScreenInfo.id);

        newScreens.splice(currentScreenIndex, 1);
        GameData.replaceCurrentGame({
            ...GameData.currentGame,
            staticScreens: this.sortScreens(newScreens)
        });

        this.setState({
            currentScreenInfo: {},
            selectedScreen: null,
            staticScreens: this.sortScreens(newScreens),
            forceUpdateVersionId: this.state.forceUpdateVersionId + 1
        })
        ApplicationState.addAlert('success', 'Screen deleted!');
    }

    async createNewScreen() {
        if (ApplicationState.currentPageHasUnsavedChanges) {
            try { 
                await this.showConfirm('Continue');
            } catch (e) { /* user cancelled, ignore and bail. */ return; }
        }
        ApplicationState.unsetCurrentPageUnsaved();

        const MaxScreens = (GameData.currentGame.smallRom ? 8 : 14);
        if (this.state.staticScreens.length >= MaxScreens) {
            ApplicationState.addAlert('warning', `Maximum number of static screens (${MaxScreens}) reached. Cannot create new screen.`);
            return;
        }

        const newScreen = {
            id: v4(),
            title: "New Screen",
            showAt: StaticScreenPositions.Unused,
            showAtDetail: null,
            data: []
        };
        for (let i = 0; i < 1024; i++) {
            newScreen.data.push(0);
        }
        const ss = JSON.parse(JSON.stringify(this.state.staticScreens));
        ss.push(newScreen);
        await new Promise(resolve => this.setState({staticScreens: this.sortScreens(ss)}, resolve));
        GameData.replaceCurrentGame({
            ...GameData.currentGame,
            staticScreens: ss
        });
        ApplicationState.addAlert('success', 'New screen created!');
        this.selectScreen(newScreen.id);
    }

    async saveChanges() {
        const newStaticScreens = JSON.parse(JSON.stringify(this.state.staticScreens));
        const index = newStaticScreens.findIndex(screen => screen.id === this.state.currentScreenInfo.id);
        if (this.state.currentScreenInfo.id === this.state.currentScreenInfo.showAtDetail && this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterScreen) {
            ApplicationState.addAlert('warning', 'Current screen is set to show after itself. Please change this before saving.');
            return;
        }

        if (this.state.currentScreenInfo.showAt !== StaticScreenPositions.Unused) {
            const similarStatics = this.state.staticScreens.filter(a => a.showAt === this.state.currentScreenInfo.showAt && a.id !== this.state.currentScreenInfo.id);
            for (let i = 0; i < similarStatics.length; i++) {
                const thisStatic = similarStatics[i];
                if (this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterLevel || this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterScreen) {
                    if (this.state.currentScreenInfo.showAtDetail === thisStatic.showAtDetail) {
                        // show alert
                        ApplicationState.addAlert('warning', 'Another screen is already set to show at this point. If you want to show multiple screens in sequence, use the "After Screen" option to do so.');
                        return;
                    }
                } else {
                    // Show alert
                    ApplicationState.addAlert('warning', 'Another screen is already set to show at this point. If you want to show multiple screens in sequence, use the "After Screen" option to do so.');
                    return;
                }
            }
        }
        if (index < 0) {
            ApplicationState.addAlert('danger', 'Selected screen somehow not found, unable to save. Please contact chris@cpprograms.net if you see this!');
            console.error('Something went very wrong, could not find screen you were editing', newStaticScreens, index, this.state.currentScreenInfo)
            return;
        }

        newStaticScreens[index] = this.state.currentScreenInfo;
        GameData.replaceCurrentGame({
            ...GameData.currentGame,
            staticScreens: newStaticScreens
        });
        ApplicationState.unsetCurrentPageUnsaved();
        ApplicationState.addAlert('success', 'Changes to level saved!');
        this.setState({forceUpdateVersionId: this.state.forceUpdateVersionId, staticScreens: this.sortScreens(GameData.currentGame.staticScreens)});
    }

    async testUnsavedChanges(event) {
        if (ApplicationState.currentPageHasUnsavedChanges) {
            event.preventDefault();
            try { 
                await this.showConfirm('Continue?');
            } catch (e) { 
                /* user cancelled, ignore and bail. */ 
                return; 
            }
        }
        ApplicationState.unsetCurrentPageUnsaved();
        event.target.closest('[href]').click();
    }

    render() {
        let previewCard = (<Card>
            <Card.Header>Screen Preview</Card.Header>
            <Card.Body>
                <Card.Text as="div">
                    <p>Click on a screen to the left, and a preview will appear here alongside settings.</p>
                    <p>You will also be able to launch a full editor from here.</p>
                </Card.Text>
            </Card.Body>
        </Card>);

        if (this.state.currentScreenInfo?.id) {
            let secondOption = <></>
            if (this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterLevel) {
                secondOption = <Form.Group>
                    <Form.Select aria-label="Level after" onChange={() => this.fieldChanged('showAtDetail')} defaultValue={this.state.currentScreenInfo.showAtDetail} id="showAtDetail">
                        {Array.from({length: 64}, (_, i) => i).map(i => (<option key={i} value={i}>Level {i+1}</option>))}
                    </Form.Select>
                </Form.Group>
            } else if (this.state.currentScreenInfo.showAt === StaticScreenPositions.AfterScreen) {
                secondOption = <Form.Group>
                    <Form.Select aria-label="Screen after" onChange={() => this.fieldChanged('showAtDetail')} defaultValue={this.state.currentScreenInfo.showAtDetail} id="showAtDetail">
                        {this.state.staticScreens.map(s => (<option key={s.id} value={s.id}>{s.title}</option>))}
                    </Form.Select>
                </Form.Group>
            }
            previewCard = <Card key={this.state.selectedScreen}>
                <Card.Header>Screen Preview</Card.Header>
                <Card.Body>
                    <Card.Text as="div">
                        <h3>{this.state.currentScreenInfo.title}</h3>
                        <div className="nam-holder">
                            <NamEditor screenId={this.state.currentScreenInfo.id} readOnly={true} />
                            <LinkContainer to={`/builder/static-screens/${this.state.currentScreenInfo.id}`}>

                                <div className="button-overlay" onClick={event => this.testUnsavedChanges(event)}>
                                    <div>
                                        <Button variant="primary">Edit</Button>
                                    </div>
                                </div>
                            </LinkContainer>
                        </div>
                        <div className="mb-2">
                            <Form.Group className="d-flex justify-content-start align-items-start">
                                <Form.Label htmlFor="title" className="me-2 lh-label">Name:</Form.Label>
                                <Form.Control type="text" aria-label="Screen Name" defaultValue={this.state.currentScreenInfo.title} onChange={() => this.fieldChanged('title')} id="title" />
                            </Form.Group>
                        </div>
                        <div className="d-flex justify-content-start align-items-start">
                            <div className="me-2 lh-label">Shown:</div>
                            <Form.Group>
                                <Form.Select aria-label="Where to show" onChange={() => this.fieldChanged('showAt')} defaultValue={this.state.currentScreenInfo.showAt} id="showAt">
                                    {Object.keys(StaticScreenPositions).map(k => <option key={k} value={StaticScreenPositions[k]}>{getScreenPositionName(k)}</option>)}
                                </Form.Select>
                            </Form.Group>
                            {secondOption}
                        </div>
                        <div className="button-bar mt-3">
                            <Button variant="outline-danger" onClick={() => this.deleteCurrentScreen()}>Delete</Button>
                            <Button className="ms-2" variant="primary" onClick={() => this.saveChanges()}>Save Changes</Button>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>
        }

        let screenList = (<li key="my-games-zero" className="d-flex justify-content-between align-items-start list-group-item text-muted">No Screens available. Click "New" below to create one.</li>)

        if (this.state.staticScreens.length > 0) {
            screenList = this.state.staticScreens.map(screen => {
                return (<ListGroup.Item action key={`screen-${screen.id}`} as="li" className="align-items-start" onClick={() => this.selectScreen(screen.id)} variant={(screen.id === this.state.selectedScreen ? 'primary' : '')}>
                    <div className="d-flex justify-content-between">
                        <div>
                            <div className="fw-bold">
                                {screen.title}
                            </div>
                            <div className="text-muted text-truncated" title={getScreenShowAtDetail(screen.showAt, screen.showAtDetail, this.state.staticScreens)}>
                                Shown: {getScreenShowAtDetail(screen.showAt, screen.showAtDetail, this.state.staticScreens)}
                            </div>
                        </div>
                    </div>
                </ListGroup.Item>)
            })
        }
        return (
            <div className="static-screen-list-page" key={this.state.forceUpdateVersionId}>
                <Modal backdrop="static" show={this.state.awaitingConfirm} onClose={() => this.cancelConfirm()} aria-labelledby="Save Changes">
                    <Modal.Header closeButton>
                        <Modal.Title>Unsaved changes</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>You have unsaved changes on the current level. If you do not save, you will lose them. Do you want to continue?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.cancelConfirm()}>Cancel</Button>
                        <Button variant="danger" onClick={() => this.continueConfirm()}>{this.state.awaitingConfirmAction}</Button>
                    </Modal.Footer>
                </Modal>
                <Modal backdrop="static" show={this.state.awaitingConfirmDelete} onClose={() => this.cancelConfirm()} aria-labelledby="Delete Screen">
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Screen</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this screen? This cannot be undone.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.cancelConfirm()}>Cancel</Button>
                        <Button variant="danger" onClick={() => this.continueConfirm()}>Delete</Button>
                    </Modal.Footer>
                </Modal>

                <Container>
                    <Row>
                        <Col>

                            <h1>Static Screens</h1>
                            <p className="mt-3">
                                This will allow you to create static screens to show anywhere in your game. They can be used to introduce game
                                mechanics, add story, add credits, or even add your own developer splash screen!
                            </p>


                        </Col>
                    </Row>
                    <Row>
                        <Col sm={4}>
                            <Card>
                                <Card.Header>Screens</Card.Header>
                                <Card.Body>
                                    <ListGroup as="ul">
                                        {screenList}
                                    </ListGroup>
                                    <div className="button-footer-bar mt-2">
                                        <Button variant="primary" onClick={() => this.createNewScreen()}>New Screen</Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={8}>
                            {previewCard}
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
}